/**
 * AI Website Booking Agent settings page (iter-12d)
 *
 * Vendor-facing surface that lets the vendor configure the AI Website Booking
 * Agent add-on:
 *   - Paste and store an OpenAI API key (encrypted at rest, never returned).
 *   - Edit the greeting the widget shows on first open.
 *   - Set up to three suggested questions and the agent's tone (TIX-019).
 *   - Edit custom instructions layered on top of the base system prompt.
 *   - Copy the embed snippet to paste into their own site.
 *   - Preview the widget in a new tab.
 *
 * The add-on must be activated first via the Add-ons page. If it is not
 * active, the API returns 409 and this page shows an activation prompt.
 *
 * Voice: "activate" and "configure" - never "premium", "upgrade", or "unlock".
 */

import { FormEvent, useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuthStore } from '../stores/authStore'
import api from '../services/api'

const WIDGET_HOST = 'https://stores.tixxly.ai'
const MAX_GREETING_LEN = 500
const MAX_INSTRUCTIONS_LEN = 4000
const MAX_PROMPT_LEN = 80
type Tone = 'friendly' | 'professional' | 'concise'
const TONES: Array<{ value: '' | Tone; label: string }> = [
  { value: '', label: 'Default: clear and helpful' },
  { value: 'friendly', label: 'Friendly: warm, like a local host' },
  { value: 'professional', label: 'Professional: polite, no small talk' },
  { value: 'concise', label: 'Concise: as brief as possible' },
]

interface AgentSettings {
  has_openai_api_key: boolean
  greeting: string | null
  custom_instructions: string | null
  public_support_email: string | null
  public_support_phone: string | null
  suggested_prompts?: string[] | null
  tone?: Tone | null
}

interface AgentApiKey {
  api_key_id: number
  key_prefix: string
  name: string
  description: string | null
  status: string
  last_used_at: string | null
  expires_at: string | null
  created_at: string
}

interface CreatedAgentApiKey extends AgentApiKey {
  raw_key: string
}

export default function AgentSettingsPage() {
  const { user } = useAuthStore()
  const vendorId = user?.vendorId
  const queryClient = useQueryClient()

  const vendorQuery = useQuery({
    queryKey: ['vendor', vendorId],
    queryFn: () => api.vendors.get(vendorId!),
    enabled: !!vendorId,
    staleTime: 5 * 60_000,
  })
  const vendorSlug =
    ((vendorQuery.data?.data as any)?.username as string | undefined) || ''

  const [openaiApiKey, setOpenaiApiKey] = useState('')
  const [greeting, setGreeting] = useState('')
  const [customInstructions, setCustomInstructions] = useState('')
  const [supportEmail, setSupportEmail] = useState('')
  const [supportPhone, setSupportPhone] = useState('')
  const [prompts, setPrompts] = useState<string[]>(['', '', ''])
  const [tone, setTone] = useState<'' | Tone>('')
  const [copyState, setCopyState] = useState<'idle' | 'copied'>('idle')
  const [saveError, setSaveError] = useState<string | null>(null)
  const [newKeyName, setNewKeyName] = useState('')
  const [newKeyDescription, setNewKeyDescription] = useState('')
  const [keyError, setKeyError] = useState<string | null>(null)
  const [freshKey, setFreshKey] = useState<CreatedAgentApiKey | null>(null)
  const [freshKeyCopied, setFreshKeyCopied] = useState(false)

  const settingsQuery = useQuery({
    queryKey: ['agent-settings', vendorId],
    queryFn: () => api.addOns.getAgentSettings(vendorId!),
    enabled: !!vendorId,
    retry: (failureCount, err: any) => {
      // 409 means add-on not active. Do not retry; render the activation prompt.
      if (err?.response?.status === 409) return false
      return failureCount < 2
    },
  })

  const settings = (settingsQuery.data?.data as any)?.data as
    | AgentSettings
    | undefined

  useEffect(() => {
    if (settings) {
      setGreeting(settings.greeting ?? '')
      setCustomInstructions(settings.custom_instructions ?? '')
      setSupportEmail(settings.public_support_email ?? '')
      setSupportPhone(settings.public_support_phone ?? '')
      const saved = settings.suggested_prompts ?? []
      setPrompts([0, 1, 2].map((i) => saved[i] ?? ''))
      setTone(settings.tone ?? '')
    }
  }, [settings])

  const save = useMutation({
    mutationFn: (payload: {
      openai_api_key?: string | null
      greeting?: string | null
      custom_instructions?: string | null
      public_support_email?: string | null
      public_support_phone?: string | null
      suggested_prompts?: string[] | null
      tone?: Tone | null
    }) => api.addOns.updateAgentSettings(vendorId!, payload),
    onSuccess: () => {
      setOpenaiApiKey('')
      setSaveError(null)
      queryClient.invalidateQueries({ queryKey: ['agent-settings', vendorId] })
    },
    onError: (err: any) => {
      setSaveError(
        err?.response?.data?.error ||
          err?.response?.data?.message ||
          'Could not save. Try again.',
      )
    },
  })

  const removeKey = useMutation({
    mutationFn: () =>
      api.addOns.updateAgentSettings(vendorId!, { openai_api_key: null }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agent-settings', vendorId] })
    },
  })

  const agentKeysQuery = useQuery({
    queryKey: ['agent-api-keys', vendorId],
    queryFn: () => api.addOns.listAgentApiKeys(vendorId!),
    enabled: !!vendorId,
  })

  const agentKeys: AgentApiKey[] =
    ((agentKeysQuery.data?.data as any)?.data as AgentApiKey[] | undefined) || []

  const activeAgentKeys = agentKeys.filter((k) => k.status === 'active')

  const createAgentKey = useMutation({
    mutationFn: (payload: { name: string; description?: string }) =>
      api.addOns.createAgentApiKey(vendorId!, payload),
    onSuccess: (res: any) => {
      const created = (res?.data?.data as CreatedAgentApiKey) || null
      setFreshKey(created)
      setFreshKeyCopied(false)
      setKeyError(null)
      setNewKeyName('')
      setNewKeyDescription('')
      queryClient.invalidateQueries({
        queryKey: ['agent-api-keys', vendorId],
      })
    },
    onError: (err: any) => {
      setKeyError(
        err?.response?.data?.error ||
          err?.response?.data?.message ||
          'Could not create key. Try again.',
      )
    },
  })

  const revokeAgentKey = useMutation({
    mutationFn: (apiKeyId: number) =>
      api.addOns.revokeAgentApiKey(vendorId!, apiKeyId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['agent-api-keys', vendorId],
      })
    },
  })

  const notActive =
    (settingsQuery.error as any)?.response?.status === 409

  if (!vendorId) {
    return (
      <div style={pageStyle}>
        <p>Sign in as a vendor to configure the AI Website Booking Agent.</p>
      </div>
    )
  }

  if (notActive) {
    return (
      <div style={pageStyle}>
        <h1 style={{ marginBottom: '0.5rem' }}>AI Website Booking Agent</h1>
        <p style={{ color: 'var(--color-gray-600)', lineHeight: 1.6 }}>
          The AI Website Booking Agent add-on is not active on this account.
          Activate it from the Add-ons page, then return here to configure the
          agent and copy the embed snippet.
        </p>
        <a
          href="/vendor/add-ons"
          style={{
            display: 'inline-block',
            marginTop: '1rem',
            padding: '0.6rem 1.2rem',
            fontSize: '0.9rem',
            fontWeight: 600,
            background: 'var(--color-tixxly-navy)',
            color: 'var(--color-pure-white)',
            border: 'none',
            borderRadius: 6,
            textDecoration: 'none',
          }}
        >
          Go to Add-ons
        </a>
      </div>
    )
  }

  if (settingsQuery.isLoading) {
    return (
      <div style={pageStyle}>
        <p>Loading agent settings...</p>
      </div>
    )
  }

  const onSubmit = (event: FormEvent) => {
    event.preventDefault()
    setSaveError(null)
    const payload: {
      openai_api_key?: string
      greeting?: string
      custom_instructions?: string
      public_support_email?: string
      public_support_phone?: string
      suggested_prompts?: string[]
      tone?: Tone | null
    } = {
      greeting,
      custom_instructions: customInstructions,
      public_support_email: supportEmail.trim(),
      public_support_phone: supportPhone.trim(),
      suggested_prompts: prompts.map((p) => p.trim()).filter(Boolean),
      tone: tone || null,
    }
    if (openaiApiKey.trim().length > 0) {
      payload.openai_api_key = openaiApiKey.trim()
    }
    save.mutate(payload)
  }

  const embedKeyValue =
    activeAgentKeys.length === 1
      ? `${activeAgentKeys[0].key_prefix}...`
      : 'YOUR_PUBLIC_AGENT_KEY'

  const embedSnippet =
    `<script async src="${WIDGET_HOST}/widget/embed.js"\n` +
    `  data-vendor="${vendorSlug}"\n` +
    `  data-api-key="${embedKeyValue}"></script>`

  const previewHref = `${WIDGET_HOST}/widget/frame/${encodeURIComponent(
    vendorSlug,
  )}`

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(embedSnippet)
      setCopyState('copied')
      window.setTimeout(() => setCopyState('idle'), 2000)
    } catch {
      setCopyState('idle')
    }
  }

  return (
    <div style={pageStyle}>
      <h1 style={{ marginBottom: '0.5rem' }}>AI Website Booking Agent</h1>
      <p
        style={{
          color: 'var(--color-gray-600)',
          marginBottom: '2rem',
          lineHeight: 1.6,
        }}
      >
        Configure the chatbot that lives on your own website. It answers
        questions about your tours and activities and completes bookings
        through Tixxly. Uses your OpenAI account for language calls.
      </p>

      <form onSubmit={onSubmit} style={{ display: 'grid', gap: '2rem' }}>
        <section style={sectionStyle}>
          <h2 style={sectionHeadingStyle}>OpenAI API key</h2>
          <p style={helperTextStyle}>
            The key is encrypted before it lands in the database and never
            returned in responses. Paste a new value to replace it; leave
            blank to keep the current key.
          </p>
          {settings?.has_openai_api_key ? (
            <div
              style={{
                background: 'var(--color-gray-50, #f7f7f7)',
                border: '1px solid var(--color-gray-200)',
                borderRadius: 6,
                padding: '0.75rem 1rem',
                marginBottom: '0.75rem',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <span style={{ fontSize: '0.9rem' }}>
                A key is stored. Enter a new value below to rotate it.
              </span>
              <button
                type="button"
                onClick={() => removeKey.mutate()}
                disabled={removeKey.isPending}
                style={secondaryButtonStyle}
              >
                {removeKey.isPending ? 'Removing...' : 'Remove key'}
              </button>
            </div>
          ) : (
            <p style={{ ...helperTextStyle, color: 'var(--color-coral-red)' }}>
              No key stored yet. The widget cannot answer messages until one is
              saved.
            </p>
          )}
          <input
            type="password"
            autoComplete="off"
            value={openaiApiKey}
            onChange={(event) => setOpenaiApiKey(event.target.value)}
            placeholder="sk-..."
            style={inputStyle}
          />
        </section>

        <section style={sectionStyle}>
          <h2 style={sectionHeadingStyle}>Greeting</h2>
          <p style={helperTextStyle}>
            The first message the widget shows when a visitor opens it. Keep it
            short and specific to your tours and activities.
          </p>
          <textarea
            value={greeting}
            onChange={(event) => setGreeting(event.target.value)}
            maxLength={MAX_GREETING_LEN}
            rows={3}
            style={textareaStyle}
            placeholder="Hi. Ask me anything about our tours and I can help you book a slot."
          />
          <div style={counterStyle}>
            {greeting.length} / {MAX_GREETING_LEN}
          </div>
        </section>

        <section style={sectionStyle}>
          <h2 style={sectionHeadingStyle}>Suggested questions</h2>
          <p style={helperTextStyle}>
            Up to three questions the widget offers under the greeting, before
            the visitor writes. Leave them empty and the widget offers general
            questions in the visitor's own language.
          </p>
          {prompts.map((value, i) => (
            <input
              key={i}
              type="text"
              value={value}
              aria-label={`Suggested question ${i + 1}`}
              onChange={(event) =>
                setPrompts((prev) => prev.map((p, j) => (j === i ? event.target.value : p)))
              }
              maxLength={MAX_PROMPT_LEN}
              style={{ ...inputStyle, marginBottom: i < 2 ? '0.5rem' : 0 }}
              placeholder={
                ['Which tour is best for first-time visitors?', 'Do you offer hotel pickup?', 'Is there availability this weekend?'][i]
              }
            />
          ))}
        </section>

        <section style={sectionStyle}>
          <h2 style={sectionHeadingStyle}>Tone of voice</h2>
          <p style={helperTextStyle}>
            How the agent sounds to visitors. It always replies in the
            visitor's language. Your logo and brand colour come from your
            storefront brand settings.
          </p>
          <select
            value={tone}
            onChange={(event) => setTone(event.target.value as '' | Tone)}
            aria-label="Tone of voice"
            style={inputStyle}
          >
            {TONES.map((t) => (
              <option key={t.value || 'default'} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </section>

        <section style={sectionStyle}>
          <h2 style={sectionHeadingStyle}>Custom instructions</h2>
          <p style={helperTextStyle}>
            Extra guidance layered on top of the base system prompt. Use this
            to encode brand voice, cancellation policy nuances, or product
            recommendations you want the agent to make.
          </p>
          <textarea
            value={customInstructions}
            onChange={(event) => setCustomInstructions(event.target.value)}
            maxLength={MAX_INSTRUCTIONS_LEN}
            rows={8}
            style={textareaStyle}
            placeholder="Always recommend the sunset tour to first-time visitors. Never quote prices in currencies other than EUR."
          />
          <div style={counterStyle}>
            {customInstructions.length} / {MAX_INSTRUCTIONS_LEN}
          </div>
        </section>

        <section style={sectionStyle}>
          <h2 style={sectionHeadingStyle}>Public support contact</h2>
          <p style={helperTextStyle}>
            The only email and phone the agent gives visitors. Leave them empty
            and the agent shares no contact details; instead it passes
            cancellations, changes, refunds and complaints to you as a request
            with the conversation attached. Your account contact is never
            shared.
          </p>
          <label style={{ display: 'block', marginBottom: '0.75rem' }}>
            <span style={helperTextStyle}>Support email</span>
            <input
              type="email"
              value={supportEmail}
              onChange={(event) => setSupportEmail(event.target.value)}
              maxLength={254}
              style={inputStyle}
              placeholder="help@yourcompany.com"
            />
          </label>
          <label style={{ display: 'block' }}>
            <span style={helperTextStyle}>Support phone, with country code</span>
            <input
              type="tel"
              value={supportPhone}
              onChange={(event) => setSupportPhone(event.target.value)}
              maxLength={40}
              style={inputStyle}
              placeholder="+44 20 7946 0000"
            />
          </label>
        </section>

        {saveError && (
          <div
            style={{
              color: 'var(--color-coral-red)',
              background: 'var(--color-coral-red-tint, #fdecec)',
              padding: '0.75rem 1rem',
              borderRadius: 6,
              fontSize: '0.9rem',
            }}
          >
            {saveError}
          </div>
        )}

        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
          <button
            type="submit"
            disabled={save.isPending}
            style={primaryButtonStyle}
          >
            {save.isPending ? 'Saving...' : 'Save settings'}
          </button>
          {save.isSuccess && !save.isPending && (
            <span style={{ color: 'var(--color-forest-green, #2d5f1a)' }}>
              Saved.
            </span>
          )}
        </div>
      </form>

      <section style={{ ...sectionStyle, marginTop: '2rem' }}>
        <h2 style={sectionHeadingStyle}>API keys</h2>
        <p style={helperTextStyle}>
          Mint a public key for the widget to authenticate against the Tixxly
          agent API. The raw key value is shown once, on creation. Store it
          somewhere safe or paste it straight into your embed snippet. Revoke
          any key that has leaked; requests using it will be rejected.
        </p>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr auto',
            gap: '0.5rem',
            alignItems: 'end',
            marginBottom: '1rem',
          }}
        >
          <div>
            <label style={labelStyle} htmlFor="agent-key-name">
              Name
            </label>
            <input
              id="agent-key-name"
              type="text"
              value={newKeyName}
              onChange={(event) => setNewKeyName(event.target.value)}
              placeholder="Production widget"
              maxLength={100}
              style={inputStyle}
            />
          </div>
          <div>
            <label style={labelStyle} htmlFor="agent-key-description">
              Description (optional)
            </label>
            <input
              id="agent-key-description"
              type="text"
              value={newKeyDescription}
              onChange={(event) => setNewKeyDescription(event.target.value)}
              placeholder="Where this key is used"
              maxLength={500}
              style={inputStyle}
            />
          </div>
          <button
            type="button"
            disabled={
              createAgentKey.isPending || newKeyName.trim().length < 3
            }
            onClick={() =>
              createAgentKey.mutate({
                name: newKeyName.trim(),
                description:
                  newKeyDescription.trim().length > 0
                    ? newKeyDescription.trim()
                    : undefined,
              })
            }
            style={primaryButtonStyle}
          >
            {createAgentKey.isPending ? 'Creating...' : 'Create key'}
          </button>
        </div>

        {keyError && (
          <div
            style={{
              color: 'var(--color-coral-red)',
              background: 'var(--color-coral-red-tint, #fdecec)',
              padding: '0.75rem 1rem',
              borderRadius: 6,
              fontSize: '0.9rem',
              marginBottom: '1rem',
            }}
          >
            {keyError}
          </div>
        )}

        {freshKey && (
          <div
            style={{
              background: 'var(--color-gray-50, #f7f7f7)',
              border: '1px solid var(--color-tixxly-navy)',
              borderRadius: 6,
              padding: '1rem',
              marginBottom: '1rem',
            }}
          >
            <p style={{ margin: '0 0 0.5rem', fontWeight: 600 }}>
              New key created. Copy the value now, it will not be shown again.
            </p>
            <pre
              style={{
                background: 'var(--color-pure-white, #fff)',
                border: '1px solid var(--color-gray-200)',
                borderRadius: 6,
                padding: '0.75rem',
                fontSize: '0.85rem',
                overflowX: 'auto',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-all',
                fontFamily:
                  'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                margin: '0 0 0.5rem',
              }}
            >
              {freshKey.raw_key}
            </pre>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(freshKey.raw_key)
                    setFreshKeyCopied(true)
                    window.setTimeout(() => setFreshKeyCopied(false), 2000)
                  } catch {
                    setFreshKeyCopied(false)
                  }
                }}
                style={secondaryButtonStyle}
              >
                {freshKeyCopied ? 'Copied' : 'Copy raw key'}
              </button>
              <button
                type="button"
                onClick={() => setFreshKey(null)}
                style={secondaryButtonStyle}
              >
                Dismiss
              </button>
            </div>
          </div>
        )}

        {agentKeysQuery.isLoading ? (
          <p style={helperTextStyle}>Loading keys...</p>
        ) : agentKeys.length === 0 ? (
          <p style={helperTextStyle}>No keys minted yet.</p>
        ) : (
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Name</th>
                <th style={thStyle}>Prefix</th>
                <th style={thStyle}>Status</th>
                <th style={thStyle}>Created</th>
                <th style={thStyle}>Last used</th>
                <th style={thStyle}></th>
              </tr>
            </thead>
            <tbody>
              {agentKeys.map((key) => (
                <tr key={key.api_key_id}>
                  <td style={tdStyle}>
                    <div style={{ fontWeight: 500 }}>{key.name}</div>
                    {key.description && (
                      <div
                        style={{
                          fontSize: '0.8rem',
                          color: 'var(--color-gray-500)',
                        }}
                      >
                        {key.description}
                      </div>
                    )}
                  </td>
                  <td style={{ ...tdStyle, fontFamily: 'ui-monospace, monospace' }}>
                    {key.key_prefix}
                  </td>
                  <td style={tdStyle}>{key.status}</td>
                  <td style={tdStyle}>
                    {new Date(key.created_at).toLocaleDateString()}
                  </td>
                  <td style={tdStyle}>
                    {key.last_used_at
                      ? new Date(key.last_used_at).toLocaleDateString()
                      : 'Never'}
                  </td>
                  <td style={tdStyle}>
                    {key.status === 'active' && (
                      <button
                        type="button"
                        disabled={revokeAgentKey.isPending}
                        onClick={() => {
                          if (
                            window.confirm(
                              `Revoke key "${key.name}"? Requests using it will be rejected.`,
                            )
                          ) {
                            revokeAgentKey.mutate(key.api_key_id)
                          }
                        }}
                        style={secondaryButtonStyle}
                      >
                        Revoke
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section style={{ ...sectionStyle, marginTop: '2rem' }}>
        <h2 style={sectionHeadingStyle}>Embed on your website</h2>
        <p style={helperTextStyle}>
          Paste this snippet just before the closing body tag on your site.
          {activeAgentKeys.length === 1
            ? ' The snippet shows the prefix of your active agent key; replace it with the full raw value shown when you created the key.'
            : ' Replace YOUR_PUBLIC_AGENT_KEY with the raw value from a key you mint above.'}
        </p>
        <pre
          style={{
            background: 'var(--color-gray-50, #f7f7f7)',
            border: '1px solid var(--color-gray-200)',
            borderRadius: 6,
            padding: '1rem',
            fontSize: '0.85rem',
            overflowX: 'auto',
            whiteSpace: 'pre',
            fontFamily:
              'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
          }}
        >
          {embedSnippet}
        </pre>
        <div
          style={{
            display: 'flex',
            gap: '0.75rem',
            marginTop: '0.75rem',
            flexWrap: 'wrap',
          }}
        >
          <button
            type="button"
            onClick={onCopy}
            style={secondaryButtonStyle}
          >
            {copyState === 'copied' ? 'Copied' : 'Copy snippet'}
          </button>
          <a
            href={previewHref}
            target="_blank"
            rel="noreferrer noopener"
            style={{ ...secondaryButtonStyle, textDecoration: 'none' }}
          >
            Preview widget
          </a>
        </div>
      </section>
    </div>
  )
}

const pageStyle = {
  padding: '2rem',
  maxWidth: 900,
  margin: '0 auto',
} as const

const sectionStyle = {
  border: '1px solid var(--color-gray-200)',
  borderRadius: 8,
  padding: '1.5rem',
  background: 'var(--color-pure-white, #fff)',
} as const

const sectionHeadingStyle = {
  fontSize: '1.05rem',
  fontWeight: 600,
  margin: '0 0 0.5rem',
} as const

const helperTextStyle = {
  margin: '0 0 0.75rem',
  color: 'var(--color-gray-500)',
  fontSize: '0.9rem',
  lineHeight: 1.5,
} as const

const inputStyle = {
  width: '100%',
  padding: '0.6rem 0.75rem',
  fontSize: '0.95rem',
  border: '1px solid var(--color-gray-300)',
  borderRadius: 6,
  boxSizing: 'border-box',
  fontFamily: 'inherit',
} as const

const textareaStyle = {
  width: '100%',
  padding: '0.6rem 0.75rem',
  fontSize: '0.95rem',
  border: '1px solid var(--color-gray-300)',
  borderRadius: 6,
  boxSizing: 'border-box',
  fontFamily: 'inherit',
  resize: 'vertical',
} as const

const counterStyle = {
  marginTop: '0.25rem',
  fontSize: '0.75rem',
  color: 'var(--color-gray-500)',
  textAlign: 'right',
} as const

const labelStyle = {
  display: 'block',
  fontSize: '0.8rem',
  fontWeight: 600,
  marginBottom: '0.25rem',
  color: 'var(--color-gray-700, #444)',
} as const

const tableStyle = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: '0.9rem',
} as const

const thStyle = {
  textAlign: 'left',
  padding: '0.5rem 0.6rem',
  borderBottom: '1px solid var(--color-gray-200)',
  fontWeight: 600,
  color: 'var(--color-gray-700, #444)',
} as const

const tdStyle = {
  padding: '0.6rem',
  borderBottom: '1px solid var(--color-gray-100, #eee)',
  verticalAlign: 'top',
} as const

const primaryButtonStyle = {
  padding: '0.6rem 1.5rem',
  fontSize: '0.95rem',
  fontWeight: 600,
  background: 'var(--color-tixxly-navy)',
  color: 'var(--color-pure-white)',
  border: 'none',
  borderRadius: 6,
  cursor: 'pointer',
} as const

const secondaryButtonStyle = {
  padding: '0.5rem 1rem',
  fontSize: '0.85rem',
  fontWeight: 600,
  background: 'var(--color-pure-white)',
  color: 'var(--color-tixxly-navy)',
  border: '1px solid var(--color-tixxly-navy)',
  borderRadius: 6,
  cursor: 'pointer',
} as const
